import useMediaQuery from "@mui/material/useMediaQuery";
import { makeStyles } from "@mui/styles";
import { useSnackbar } from "notistack";
import { useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useForgotPassword } from "../../../API/Hooks";
import { useLogin } from "../../../API/Hooks/Auth";
import { AuthBrandPanel } from "../../../components/AuthBrandPanel";
import { LoginForm } from "../../../components/LoginForm";
import { SentResetToEmail } from "../../../components/SentResetToEmail";
import { LoginMessageContext } from "../../../contexts/LoginMessage";
import { loadItem } from "../../../utils/localStorage";

const useStyles = makeStyles({
  page: {
    display: "flex",
    width: "100%",
    minHeight: "100vh",
    background: "#050821",
  },
  formPanel: {
    width: "42%",
    minWidth: 360,
    maxWidth: 560,
    backgroundColor: "#FFFFFF",
    borderTopRightRadius: 80,
    borderBottomRightRadius: 80,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 56px",
    boxSizing: "border-box",
  },
  brandPanel: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 40,
  },
  pageMobile: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minHeight: "100vh",
    background: "#050821",
  },
  formPanelMobile: {
    width: "100%",
    backgroundColor: "#FFFFFF",
    borderBottomLeftRadius: 40,
    borderBottomRightRadius: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 24px 36px",
    boxSizing: "border-box",
    flex: "1 0 auto",
  },
  brandPanelMobile: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "28px 24px 40px",
  },
});

const Login = () => {
  const isMobile = useMediaQuery("(max-width:900px)");

  const { enqueueSnackbar } = useSnackbar();

  const classes = useStyles();

  const { fetchLogin, requestError, requestLoading } = useLogin();

  const [login, dispatchLoginMessageContext] = useContext(LoginMessageContext);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const { fetchForgotPassword, requestErrorReset, requestLoadingReset } =
    useForgotPassword();

  const [emailReset, setEmailReset] = useState("");

  const [forgotPassword, setForgotPassword] = useState(false);

  const [logging, setLogging] = useState(false);

  const [forgot, setForgot] = useState(false);

  const handleLogin = (data) => {
    data.username = email;
    data.password = password;
    const loginRequest = fetchLogin(data);
    if (loginRequest?.catch) {
      loginRequest.catch(() => {});
    }
    setLogging(true);
  };

  const handleReset = (data) => {
    if (!requestLoadingReset) {
      fetchForgotPassword(data);
    }
    setForgot(true);
  };

  const handleCancel = () => {
    setForgotPassword(false);
    setEmailReset("");
  };

  useEffect(() => {
    const showMessage = () => {
      if (
        !requestError &&
        !requestLoading &&
        logging &&
        email !== "" &&
        password !== ""
      ) {
        dispatchLoginMessageContext({
          type: "setLoginMessage",
          canShow: true,
        });
        setLogging(false);
      }
    };
    showMessage();

    const showMessageAnother = () => {
      if (
        !requestErrorReset &&
        !requestLoadingReset &&
        forgot &&
        emailReset !== ""
      ) {
        enqueueSnackbar(`Link de recuperação de senha enviado!`, {
          variant: "success",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
        });
        setForgot(false);
      }
    };
    showMessageAnother();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    requestError,
    logging,
    requestLoading,
    email,
    password,
    forgot,
    forgotPassword,
    requestErrorReset,
    requestLoadingReset,
  ]);

  if (loadItem("token")) {
    return <Navigate to="/" />;
  }

  const formContent = forgotPassword ? (
    <SentResetToEmail
      emailReset={{
        id: "emailReset",
        value: emailReset,
      }}
      handleReset={handleReset}
      setEmailReset={setEmailReset}
      handleCancel={handleCancel}
    />
  ) : (
    <LoginForm
      email={{
        id: "email",
        value: email,
      }}
      password={{
        id: "password",
        value: password,
      }}
      handleLogin={handleLogin}
      setPassword={setPassword}
      setEmail={setEmail}
      setForgotPassword={setForgotPassword}
    />
  );

  if (isMobile) {
    return (
      <div className={classes.pageMobile}>
        <div className={classes.formPanelMobile}>{formContent}</div>
        <div className={classes.brandPanelMobile}>
          <AuthBrandPanel isMobile />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.page}>
      <div className={classes.formPanel}>{formContent}</div>
      <div className={classes.brandPanel}>
        <AuthBrandPanel />
      </div>
    </div>
  );
};

export default Login;
